import { useInfiniteQuery } from '@tanstack/react-query';

import { ProfileSection } from '@/components/profile/ProfileSection';
import { ProfileItemMeta } from '@/components/profile/ProfileText';
import { Badge, BadgeText } from '@/components/ui/badge';
import { HStack } from '@/components/ui/hstack';
import { Link, LinkText } from '@/components/ui/link';
import { VStack } from '@/components/ui/vstack';
import {
  type ProfileTrialRow,
  getProfileTrialsInfiniteQueryOptions,
} from '@/services/getUserClinicalTrials';
import { getAccessibilityProps } from '@/util/accessibilityProps';
import { trackProfileClinicalTrialOpened } from '@/util/analyticsEvents';
import {
  type TrialStatusTone,
  formatTrialPhase,
  formatTrialStatus,
  trialStatusTone,
} from '@/util/formatClinicalTrial';
import { formatPublicationDate } from '@/util/formatPublicationDate';

interface ClinicalTrialsSectionProps {
  expertId: number;
  /**
   * Trials at the expert's institution in their focus areas. Pass 0 while the
   * institutional_trials flag is off: they are fetched and listed only when this
   * is above zero, so with the flag off the section reads as the expert's own
   * trials alone.
   */
  institutionalCount: number;
  /** Names the institution on its trials. Falls back to "Institution" when missing. */
  institutionName?: string | null;
  /**
   * Called when the user collapses an expanded list via "Show less". The
   * parent uses this to scroll the section header back into view on platforms
   * where DOM-based scrollIntoView is not available.
   */
  onCollapseScroll?: () => void;
}

/**
 * Badge action and text color for each status tone. The text is a step darker than the
 * badge default so it clears 4.5:1 on its tint.
 */
const STATUS_BADGE: Record<
  TrialStatusTone,
  { action: 'success' | 'error' | 'muted'; text: string }
> = {
  success: { action: 'success', text: 'text-success-700' },
  error: { action: 'error', text: 'text-error-700' },
  muted: { action: 'muted', text: 'text-typography-700' },
};

/**
 * ClinicalTrialsSection - expert profile clinical trials.
 *
 * One list, newest first, merged and paged by the server. The trials the expert is
 * credited on and, when institutional trials are on, the ones listed at their
 * institution. Each row says
 * how it relates to the expert: their documented role on a trial that is theirs,
 * or the institution's name on one that is the institution's, which names no role
 * since the listing says nothing about who works on it.
 *
 * Phase and status are badges, with the status in green while the trial is running,
 * red once suspended or terminated, and gray otherwise. Rows link out to
 * ClinicalTrials.gov, and a missing title falls back to the NCT id.
 */
export function ClinicalTrialsSection({
  expertId,
  institutionalCount,
  institutionName,
  onCollapseScroll,
}: ClinicalTrialsSectionProps) {
  const { data, isLoading, isError, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery(getProfileTrialsInfiniteQueryOptions(expertId, institutionalCount > 0));

  const trials: ProfileTrialRow[] = data?.pages.flatMap((page) => page?.clinicalTrials ?? []) ?? [];

  const total = data?.pages[0]?.total ?? trials.length;

  const institutionText = institutionName || 'Institution';

  return (
    <ProfileSection<ProfileTrialRow>
      title="Clinical Trials"
      isLoading={isLoading}
      isError={isError}
      onRetry={() => refetch()}
      items={trials}
      total={total}
      hasNextPage={hasNextPage}
      isFetchingNextPage={isFetchingNextPage}
      onLoadMore={() => fetchNextPage()}
      emptyText="No clinical trials listed yet."
      expertId={expertId}
      sectionKey="trials"
      onCollapseScroll={onCollapseScroll}
      keyExtractor={(trial) => `${trial.origin}-${trial.id}`}
      renderItem={(trial) => {
        const phase = formatTrialPhase(trial.phase);

        const status = formatTrialStatus(trial.status);

        const date = formatPublicationDate(trial.year, trial.month);

        const tone = STATUS_BADGE[trialStatusTone(trial.status)];

        const isInstitutional = trial.origin === 'institution';

        const relation = isInstitutional ? institutionText : trial.roleLabel;

        return (
          <VStack className="gap-2">
            <Link
              href={trial.url}
              isExternal
              onPress={() =>
                trackProfileClinicalTrialOpened({
                  expertId,
                  trialId: trial.id,
                  nctId: trial.nctId,
                  trialTitle: trial.title,
                  trialYear: trial.year,
                  isInstitutional,
                })
              }
              {...getAccessibilityProps({
                role: 'link',
                label: `${trial.title || trial.nctId}, opens in a new tab`,
              })}>
              <LinkText size="md" className="text-primary-600 data-[hover=true]:text-primary-700">
                {trial.title || trial.nctId}
              </LinkText>
            </Link>
            {phase || status || relation || date ? (
              <HStack className="flex-wrap items-center gap-x-2 gap-y-1.5">
                {phase ? (
                  <Badge size="sm" action="muted" className="bg-secondary-50" testID="trial-phase">
                    <BadgeText className="text-secondary-800">{phase}</BadgeText>
                  </Badge>
                ) : null}
                {status ? (
                  <Badge size="sm" action={tone.action} testID="trial-status">
                    <BadgeText className={tone.text}>{status}</BadgeText>
                  </Badge>
                ) : null}
                {relation || date ? (
                  // One group, so on a narrow screen the relation, dot and date wrap
                  // together and the dot never hangs at the end of a line.
                  <HStack className="min-w-0 shrink items-center gap-x-2">
                    {relation ? (
                      <HStack className="min-w-0 shrink items-center gap-1">
                        <ProfileItemMeta
                          className="shrink"
                          testID={isInstitutional ? 'trial-institution' : 'trial-role-label'}>
                          {relation}
                        </ProfileItemMeta>
                      </HStack>
                    ) : null}
                    {relation && date ? <ProfileItemMeta>·</ProfileItemMeta> : null}
                    {date ? <ProfileItemMeta className="shrink-0">{date}</ProfileItemMeta> : null}
                  </HStack>
                ) : null}
              </HStack>
            ) : null}
          </VStack>
        );
      }}
    />
  );
}
